import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const validate = () => {
    const next = { email: '', password: '' };
    let hasError = false;

    if (!email) {
      next.email = 'Email is required';
      hasError = true;
    }
    if (!password) {
      next.password = 'Password is required';
      hasError = true;
    }

    setFieldErrors(next);
    return !hasError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) return;

    try {
      setSubmitting(true);
      await login(email, password);
      // After login, always go through Sites → Onboarding decision
      navigate('/sites');
    } catch (err) {
      setError(err.message || 'Unable to sign you in. Please check your details.');
    } finally {
      setSubmitting(false);
    }
  };

  const logoUrl =
    'https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png';
  const leftBg =
    'https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=1600';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Grid container sx={{ minHeight: '100vh' }}>
        {/* Left side – background image + branding (Dexign-style) */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            // width: '40%',
            p: 15,
            backgroundImage: `linear-gradient(to bottom, rgba(15,23,42,0.75), rgba(15,23,42,0.9)), url(${leftBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: '#fff',
          }}
        >
          <Box>
            <Box sx={{ mb: 4 }}>
              <img src={logoUrl} alt="RTNZERO" style={{ width: 140 }} />
            </Box>
            <Box sx={{ maxWidth: 420 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
                Check the status of every site in one view.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                RTNZERO gives restaurant teams a single workspace for live sales, carbon, energy and
                labour – optimised for busy service, not dashboards.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 4, fontSize: 12, opacity: 0.85 }}>
            <Box sx={{ display: 'flex', gap: 3, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2">Privacy Policy</Typography>
              <Typography variant="body2">Support</Typography>
            </Box>
            <Typography variant="body2">© {new Date().getFullYear()} RTNZERO</Typography>
          </Box>
        </Grid>

        {/* Right side – auth card */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 6 },
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={8}
              sx={{
                p: 4,
                borderRadius: 3,
                boxShadow: '0 20px 60px rgba(15,23,42,0.65)',
              }}
            >
              <Box sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                  Welcome to RTNZERO
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign in with your workspace email to continue.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  autoComplete="email"
                  autoFocus
                />

                <TextField
                  margin="normal"
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!fieldErrors.password}
                  helperText={fieldErrors.password}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    New to RTNZERO?{' '}
                    <Link to="/organisation" style={{ color: '#144bb8', textDecoration: 'none' }}>
                      Request access
                    </Link>
                  </Typography>
                  <Link
                    to="/forgot-password"
                    style={{ color: '#9ca3af', fontSize: 12, textDecoration: 'none' }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    mt: 1,
                    mb: 1,
                    py: 1.4,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </Box>
            </Paper>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Login;
